<template>
  <div stripe-content-player-head-component :style="containerStyle" class="bg-cover-center">
    <div v-if="content.background_image" class="position-absolute-center filter-blur"></div>
    <div class="container player-container position-relative">
      <div class="row">
        <slot name="player-body"></slot>
        <div class="col-12 p-0 player-container__content">
          <div class="slider-navigation slider-navigation--prev d-md-none" @click="slider.previous()"></div>
          <div class="slider-navigation slider-navigation--next d-md-none" @click="slider.next()"></div>
          <div class="actions actions--top mt-3 mr-3">
            <div @click="$emit('visible', false)" class="cursor-pointer skinned d-flex justify-content-center align-items-center">
              <i class="fa-light fa-close"></i>
            </div>
          </div>
          <div class="actions actions--bottom mr-3">
            <modal-share v-if="content.share" :content="content" :opt-container-content="containerContent">
              <template #share-icon="{handleShareClick}">
                <div @click="handleShareClick" class="cursor-pointer skinned mb-3 d-flex justify-content-center align-items-center">
                  <i class="fa-light fa-share-nodes"></i>
                </div>
              </template>
            </modal-share>
            <div v-if="content.favourite" class="cursor-pointer skinned d-flex justify-content-center align-items-center">
              <i 
                :class="isFavourite(containerContent, content.favourite.interaction) ? 'fas' : 'fal'"
                class="fa-bookmark"
                @click="mayUpdateFavouriteInteraction(containerContent, content.favourite, content.favourite.interaction)"
              ></i>
            </div>
          </div>
          <div class="overlay overlay--top"></div>
          <div class="overlay overlay--bottom d-md-none"></div>
          <div class="player-stories">
            <div class="player__header">
              <div class="player__pagination px-3">
                <div
                  v-for="(story, index) in content.stories"
                  :key="`pagination-${story.$id}`"
                  class="player__pagination-bullet"
                  :class="[`player__pagination-bullet--${index}`]"
                >
                  <span></span>
                </div>
              </div>
            </div>
            <div class="player-stories__content flickity-carousel" ref="flickity-carousel">
              <div 
                v-for="(story, index) in content.stories"
                :key="`story-${story.$id}`"
                class="flickity-carousel-item d-flex align-items-center justify-content-center p-0">
                <component
                  :is="story.layout"
                  :content="story"
                  :active-story="visible && currentIndex===index"
                  :opt-container-content="optContainerContent"
                  body-scroll-lock-ignore
                  class="story"
                ></component>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div> 
</template>

<script>
import { fandomBasicMixin } from 'src/modules/fandom_mixins_module.js';
import { interactionMixin } from "src/modules/interactions_module.js";
import { VodafoneLab } from "../../../modules/vodafone_lab_module.js";
import anime from 'animejs/lib/anime.es.js';
import Flickity from "flickity";

export default {
  mixins: [fandomBasicMixin, interactionMixin],
  props: {
    visible: {
      type: Boolean,
      default: true
    },
    bodyExpanded: {
      type: Boolean,
      default: false
    }
  },
  data: function() {
    return {
      slider: null,
      isPlaying: false,
      currentIndex: 0,
      prevIndex: -1,
      progressTimeline: null,
      progressTimelineAnimation: false,
      userTriggerNavigation: false
    };
  },
  mounted() {
    Vue.nextTick(() => {
      this.initSlider();
      this.initProgressTimeline();

      if (this.visible) {
        this.togglePlay();
        this.executeDigitalData();
      }
      
      Fandom.onEvent('content-player-pause', this.progressTimeline.pause);
      Fandom.onEvent('contact-form-modal-hidden', this.progressTimeline.play);
    })
  },
  methods:{
    getContentResponsiveImageURL: Fandom.getContentResponsiveImageURL,
    mayUpdateFavouriteInteraction() {
      VodafoneLab.addDigitalDataEvent("content_save", "tools", `:Save:${this.containerContent.id}`);
      if (Fandom.isUserRegistered()) {
        this.updateFavouriteInteraction(this.containerContent, this.content.favourite, this.content.favourite.interaction)
      } else {
        Fandom.emit('toggle-registration-modal');
      }
    },
    initProgressTimeline() {
      this.progressTimeline = anime.timeline({
        autoplay: false,
        easing: 'linear',
      });

      this.content.stories.forEach((story, index) => {
        this.progressTimeline.add({
          targets: `.player__pagination-bullet--${index} span`,
          translateX: ['-101%', '0%'],
          duration: (story.duration || 8000),
          changeComplete: this.completeStoryProgress
        })
      });
    },
    togglePlay() {
      const fn = this.isPlaying ? 'pause' : 'play';
      this.progressTimeline[fn]();
      this.isPlaying = !this.isPlaying;
    },
    resetSlider() {
      this.userTriggerNavigation = true;
      this.progressTimeline.pause();
      this.progressTimeline.seek(0);
      this.isPlaying = false;
      this.currentIndex = 0;
      this.slider.select(0, true, true);
    },
    initSlider() {
      this.slider = new Flickity(this.$refs["flickity-carousel"], {
        draggable: false,
        contain: true,
        pageDots: false,
        cellAlign: "center",
        prevNextButtons: true,
        on: {
          change: (index) => this.sliderChange(index),
          pointerDown: () => {
            if (!this.progressTimeline.completed) {
              this.progressTimeline.pause();
            }
          },
          pointerUp: () => {
            if (!this.progressTimeline.completed) {
              this.progressTimeline.play();
            }
          },
          scroll: () => this.sliderScroll()
        }
      });
    },
    sliderScroll() {
      const stories = $('.flickity-carousel-item .story');
      this.slider?.slides.forEach( ( slide, i ) => {
        const story = stories[i];
        const x = ( slide.target + this.slider.x ) * -1/3;
        story.style.transform = `translateX(${x}px)`;
      });
    },
    sliderChange(index) {
      this.currentIndex = index;
      this.executeDigitalData();

      if (this.visible) {
        if (this.progressTimelineAnimation) {
          this.progressTimelineAnimation = false;
        } else {
          this.userTriggerNavigation = true;
          this.progressTimeline.pause();
          const seekTo = this.progressTimeline.children[this.currentIndex].timelineOffset;
          this.progressTimeline.seek(seekTo);
          this.progressTimeline.play();
        }

        this.$emit('last-slide', this.currentIndex === this.totalStories - 1);
      }
    },
    executeDigitalData() {
      if (!!window.wa_gc) {
        const digitalData = Fandom.deepClone(window.digitalData);
        digitalData.page.pageInfo.pageName = this.content.stories[this.currentIndex].digital_data_page_name;
        window.wa_gc(digitalData, "view");
        console.log("%cExecuted wa_gc(digitalData, 'view')", "background: #222; color: #bada55; padding: .5rem;", digitalData);
      }
    },
    completeStoryProgress() {
      if (this.currentIndex === this.totalStories - 1) {
        // nothing to do
      } else if (!this.userTriggerNavigation) {
        this.progressTimelineAnimation = true;
        this.slider.next();
      } else if (this.userTriggerNavigation) {
        this.userTriggerNavigation = false;
      }
    }
  },
  computed: {
    totalStories() {
      return this.slider?.slides.length;
    },
    containerStyle() {
      return this.content.background_image ? {'background-image': this.getContentResponsiveImageURL('background_image')} : { "background-color": this.content.background_color };
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.slider) {
        this.slider.resize();
        this.togglePlay();
      } else if (!newVal) {
        this.resetSlider();
      }
    },
    bodyExpanded(newVal) {
      if (this.visible) {
        if (newVal && this.progressTimeline.progress === 100) {
          // nothing to do
        } else {
          this.togglePlay();
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
[stripe-content-player-head-component] {
  background-color: $dark;
  color: $white;

  ::v-deep .share-modal {
    color: var(--dark);
  }

  .filter-blur {
    backdrop-filter: blur(30px);
  }

  .player-container {
    padding-top: var(--box-size);
    padding-bottom: var(--box-size);

    @include media-breakpoint-up(sm) {
      .row:first-child {
        overflow: hidden;
        border-radius: 0.5rem;
        position: relative;
      }
    }
  }

  .overlay {
    position: absolute;
    left: 0;
    right: 0;
    opacity: .75;
    z-index: var(--player-z-index);

    &--top {
      top: 0;
      height: 3.5rem;
      background: linear-gradient(180deg, rgba(0, 0, 0, .65) 0%, rgba(0, 0, 0, 0) 100%);
    }
    
    &--bottom {
      bottom: 0;
      height: 8rem;
      background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, .65) 100%);
    }
  }

  .slider-navigation {
    width: 25vw;
    z-index: var(--player-z-index);
    position: absolute;
    top: 0;
    bottom: 0;

    &--next {
      right: 0;
    }

    &--prev {
      left: 0;
    }
  }

  .actions {
    z-index: calc(var(--player-z-index) + 1);
    position: absolute;
    right: 0;

    &--bottom {
      bottom: calc(var(--handle-height) + 1rem);
    }

    &--top {
      top: 0;
    }

    .skinned {
      width: 2.5rem;
      height: 2.5rem;
      text-align: center;
      border-radius: 100%;
      background-color: rgba($gray-500, .65);

      i {
        color: $white;
        text-shadow: 1px 1px 3px rgba(0, 0, 0, .5);
        font-size: 18px;
      }
    }
  }

  ::v-deep .flickity-prev-next-button {
    @include media-breakpoint-down(sm) {
      display: none;
    }
  }

  .flickity-carousel-item {
    width: 100%;
    height: calc(var(--viewport-height) - (2 * var(--box-size)));
    padding: 1.5rem;
    overflow: hidden;

    .story {
      height: 100%;
    }

    ::v-deep { 
      .h-100vh-header {
        height: calc(var(--viewport-height) - (2 * var(--box-size)));
      }
    }
  }

  .player__pagination {
    position: absolute;
    left: 0;
    right: 0;
    top: 8px;
    display: flex;
    justify-content: space-between;
    z-index: var(--player-z-index);
    pointer-events: none;

    &-bullet {
      width: 100%;
      flex-shrink: 10;
      height: 3px;
      background: rgba(244,244,244,.25);
      position: relative;
      overflow: hidden;

      span {
        position: absolute;
        background: $white;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        transform: translateX(-101%);
      }

      &+.player__pagination-bullet {
        margin-left: 3px;
      }
    }
  }
}
</style>